import type { JSX } from 'react';
import type { TicketPreview } from '../api.js';

/**
 * Предпросмотр билета продукта: замечания к событию, Wallet профиля, картинка
 * билета и макет карточки Wallet. Открывается отдельной страницей
 * (pages/TicketPreviewPage.tsx) из блока «Событие билета» в карточке продукта.
 *
 * Несохранённые поля формы передаются через localStorage, а не в адресе:
 * design.md бывает длинным. Ключ живёт час — страницу можно обновить.
 */

const PREVIEW_PREFIX = 'pl_ticket_preview:';
const PREVIEW_TTL_MS = 60 * 60 * 1000;

/** Сохранить поля формы для страницы предпросмотра; null — браузер не дал записать. */
export function savePreviewPayload(payload: Record<string, unknown>): string | null {
  try {
    const now = Date.now();
    // Прибираем старые ключи, чтобы предпросмотры не копились.
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const k = localStorage.key(i);
      if (!k?.startsWith(PREVIEW_PREFIX)) continue;
      const ts = Number(k.slice(PREVIEW_PREFIX.length).split('-')[0]);
      if (!Number.isFinite(ts) || now - ts > PREVIEW_TTL_MS) localStorage.removeItem(k);
    }
    const key = `${now}-${Math.random().toString(36).slice(2, 10)}`;
    localStorage.setItem(PREVIEW_PREFIX + key, JSON.stringify(payload));
    return key;
  } catch {
    return null;
  }
}

/** Поля формы для страницы предпросмотра; null — ключа нет или он устарел. */
export function loadPreviewPayload(key: string): Record<string, unknown> | null {
  try {
    const raw = localStorage.getItem(PREVIEW_PREFIX + key);
    return raw ? (JSON.parse(raw) as Record<string, unknown>) : null;
  } catch {
    return null;
  }
}

// Пресеты WalletWallet — примерные цвета для макета карточки.
const PRESET_BG: Record<string, string> = {
  dark: '#1c1c1e',
  blue: '#1e40af',
  green: '#15803d',
  red: '#b91c1c',
  purple: '#6d28d9',
  orange: '#c2410c',
};

/** Текст на фоне: WalletWallet подбирает его по яркости — повторяем примерно. */
function textOn(bg: string): string {
  const m = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(bg);
  if (!m) return '#ffffff';
  const [r, g, b] = [m[1], m[2], m[3]].map((h) => parseInt(h!, 16) / 255);
  return 0.2126 * r! + 0.7152 * g! + 0.0722 * b! > 0.55 ? '#111111' : '#ffffff';
}

function PassField({ f, big }: { f: { label?: string; value: string }; big?: boolean }): JSX.Element {
  return (
    <div style={{ minWidth: 0 }}>
      {f.label && (
        <div style={{ fontSize: 9.5, textTransform: 'uppercase', letterSpacing: 0.4, opacity: 0.75 }}>
          <bdi>{f.label}</bdi>
        </div>
      )}
      <div style={{ fontSize: big ? 18 : 12.5, fontWeight: big ? 500 : 400, lineHeight: 1.25 }}>
        <bdi>{f.value}</bdi>
      </div>
    </div>
  );
}

/** Предпросмотр: замечания, Wallet профиля, картинка билета и макет карточки. */
export function TicketPreviewView({ preview }: { preview: TicketPreview }): JSX.Element {
  const pass = preview.pass;
  const bg = pass?.color ?? PRESET_BG[pass?.colorPreset ?? 'dark'] ?? '#1c1c1e';
  const fg = textOn(bg);
  const walletNote =
    preview.wallet === 'on'
      ? 'Wallet в настройках профиля включён — карточка выпустится после оплаты.'
      : preview.wallet === 'off'
        ? 'Wallet в настройках профиля выключен — карточки не будет, остальные кнопки будут.'
        : 'В настройках профиля нет ключа WalletWallet — карточки не будет, остальные кнопки будут.';
  return (
    <div className="stack" style={{ gap: 12 }}>
      {preview.problems.length === 0 ? (
        <div style={{ fontSize: 12, color: 'var(--ok-fg, #2e7d32)' }}>Событие заполнено — кнопки билета будут.</div>
      ) : (
        preview.problems.map((p) => (
          <div key={p} style={{ fontSize: 12, color: 'var(--danger-fg)' }}>
            {p}
          </div>
        ))
      )}
      <div style={{ fontSize: 12, color: preview.wallet === 'on' ? 'var(--fg-subtle)' : 'var(--danger-fg)' }}>
        {walletNote}
      </div>
      {(preview.image || pass) && (
        <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'flex-start' }}>
          {preview.image && (
            <img
              src={preview.image}
              alt="Картинка билета"
              style={{ width: 360, maxWidth: '100%', borderRadius: 12, border: '1px solid var(--border)' }}
            />
          )}
          {pass && (
            <div style={{ display: 'grid', gap: 10, width: 320, maxWidth: '100%' }}>
              <div
                style={{
                  background: bg,
                  color: fg,
                  borderRadius: 14,
                  padding: 12,
                  display: 'grid',
                  gap: 10,
                  boxShadow: '0 1px 3px rgba(0,0,0,.15)',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                  {pass.logoURL ? (
                    <img src={pass.logoURL} alt="" style={{ height: 26, maxWidth: 110, objectFit: 'contain' }} />
                  ) : pass.wideLogoURL ? (
                    <img src={pass.wideLogoURL} alt="" style={{ height: 22, maxWidth: 130, objectFit: 'contain' }} />
                  ) : (
                    <div style={{ fontSize: 13, fontWeight: 600 }}>
                      <bdi>{pass.logoText}</bdi>
                    </div>
                  )}
                  {pass.headerFields?.[0] && (
                    <div style={{ textAlign: 'right' }}>
                      <PassField f={pass.headerFields[0]} />
                    </div>
                  )}
                </div>
                {pass.stripURL && (
                  <img src={pass.stripURL} alt="" style={{ width: '100%', height: 70, objectFit: 'cover', borderRadius: 6 }} />
                )}
                {pass.primaryFields?.[0] && <PassField f={pass.primaryFields[0]} big />}
                <div style={{ display: 'flex', gap: 14 }}>
                  {pass.secondaryFields?.map((f) => <PassField key={f.label ?? f.value} f={f} />)}
                </div>
                {preview.qr && (
                  <div style={{ justifySelf: 'center', background: '#fff', borderRadius: 8, padding: 6, textAlign: 'center' }}>
                    <img src={preview.qr} alt="QR" style={{ width: 96, height: 96, display: 'block' }} />
                    <div style={{ fontSize: 10, color: '#111' }}>{pass.barcodeAltText}</div>
                  </div>
                )}
              </div>
              {!!pass.backFields?.length && (
                <div style={{ fontSize: 11.5, display: 'grid', gap: 4 }}>
                  <div style={{ color: 'var(--fg-subtle)' }}>Оборот карточки:</div>
                  {pass.backFields.map((f, i) => (
                    <div key={i}>
                      {f.label && <span style={{ color: 'var(--fg-subtle)' }}>{f.label}: </span>}
                      <bdi>{f.value}</bdi>
                    </div>
                  ))}
                </div>
              )}
              <div style={{ fontSize: 11, color: 'var(--fg-subtle)' }}>
                Макет примерный: так карточку видит Pro WalletWallet. На бесплатном тарифе она синяя, без своего
                цвета и картинок. Имя и номер — пример.
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
